// Generado desde design-system/components/feedback/Toast.tsx — no editar; edita el .ts/.tsx y corre build-ds.
import { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { AlertTriangle, CheckCircle2, Info, OctagonAlert, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Spinner } from '../core/Spinner';
import { DUR, useIsoLayoutEffect, useReducedMotion } from '../motion/motion-utils';
import './feedback-styles';
export const TOAST_TONES = {
    success: { icon: CheckCircle2, chip: 'bg-pos-soft text-pos', bar: 'bg-pos', sr: 'Éxito', live: 'polite', duration: 5000 },
    info: { icon: Info, chip: 'bg-accent-soft text-accent-ink', bar: 'bg-accent', sr: 'Información', live: 'polite', duration: 5000 },
    warning: { icon: AlertTriangle, chip: 'bg-warn-soft text-warn', bar: 'bg-status-warning', sr: 'Advertencia', live: 'assertive', duration: 8000 },
    error: { icon: OctagonAlert, chip: 'bg-neg-soft text-neg', bar: 'bg-neg', sr: 'Error', live: 'assertive', duration: Infinity },
    loading: { icon: null, chip: 'bg-accent-soft text-accent-ink', bar: 'bg-accent', sr: 'En proceso', live: 'polite', duration: Infinity },
};
function ActionButton({ onClick, className, children }) {
    return (<button type="button" onClick={onClick} className={cn('inline-flex h-8 shrink-0 items-center rounded-full px-3 text-body-sm font-semibold text-accent-ink', 'transition-[transform,background-color] duration-fast ease-out-ios hover:bg-accent-soft active:scale-[.96]', 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring', className)}>
      {children}
    </button>);
}
/**
 * Toast — la tarjeta visual (vidrio fuerte: es una capa flotante).
 * Úsala sola solo en documentación o miniaturas; en la app usa useToast().
 */
export const Toast = forwardRef(function Toast({ tone = 'info', title, description, action, onAction, onClose, closeLabel = 'Cerrar notificación', duration, paused = false, contentHidden = false, className, style, ...rest }, ref) {
    const t = TOAST_TONES[tone] ?? TOAST_TONES.info;
    const Icon = t.icon;
    const showCountdown = typeof duration === 'number' && Number.isFinite(duration) && duration > 0;
    return (<div ref={ref} className={cn('db-glass-strong relative flex w-full items-start gap-3 overflow-hidden rounded-lg py-3 pl-3 pr-2 text-left', className)} style={style} {...rest}>
      <div className={cn('flex min-w-0 flex-1 items-start gap-3 transition-opacity duration-base ease-out-ios', contentHidden && 'opacity-0')}>
        <span className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-full', t.chip)} aria-hidden="true">
          {Icon ? <Icon size={18} strokeWidth={2}/> : <Spinner size={18}/>}
        </span>
        <div className="min-w-0 flex-1 pt-[7px]">
          <p className="text-body-sm font-semibold leading-5 text-ink">
            <span className="sr-only">{t.sr}: </span>
            {title}
          </p>
          {description && <p className="mt-0.5 text-body-sm text-ink-2">{description}</p>}
          {action && description && (<ActionButton onClick={onAction} className="-ml-3 mt-1.5">
              {action.label}
            </ActionButton>)}
        </div>
        {action && !description && (<ActionButton onClick={onAction} className="mt-0.5">
            {action.label}
          </ActionButton>)}
        {onClose && (<button type="button" onClick={onClose} aria-label={closeLabel} title={closeLabel} className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-full text-ink-3', 'transition-[transform,background-color,color] duration-fast ease-out-ios hover:bg-hair hover:text-ink active:scale-[.92]', 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring')}>
            <X size={16} strokeWidth={2} aria-hidden="true"/>
          </button>)}
      </div>
      {showCountdown && (<span className="pointer-events-none absolute inset-x-4 bottom-0 h-[2px] overflow-hidden rounded-full bg-hair" aria-hidden="true">
          <i className={cn('dbf-countdown block h-full rounded-full opacity-70', t.bar)} style={{ animationDuration: `${duration}ms`, animationPlayState: paused ? 'paused' : 'running' }}/>
        </span>)}
    </div>);
});
const ToastContext = createContext(null);
let seq = 0;
/**
 * ToastProvider — envuelve la app una vez. `max` = toasts visibles en la pila.
 */
export function ToastProvider({ children, max = 3 }) {
    const [toasts, setToasts] = useState([]); // el más nuevo primero
    const timers = useRef(new Map());
    const remove = useCallback((id) => {
        setToasts((ts) => ts.filter((t) => t.id !== id));
    }, []);
    const dismiss = useCallback((id, how = 'fade') => {
        setToasts((ts) => ts.map((t) => (id == null || t.id === id ? { ...t, exiting: how } : t)));
        const ids = id == null ? null : [id];
        const tm = setTimeout(() => {
            setToasts((ts) => ts.filter((t) => (ids ? !ids.includes(t.id) : !t.exiting)));
        }, DUR.close);
        timers.current.set(`${id}`, tm);
    }, []);
    const show = useCallback((opts) => {
        const id = opts.id ?? `t${++seq}`;
        const tone = opts.tone ?? 'info';
        const base = TOAST_TONES[tone] ?? TOAST_TONES.info;
        const duration = opts.duration ?? (opts.action ? Math.max(base.duration, 8000) : base.duration);
        const toast = { ...opts, id, tone, duration, createdAt: Date.now(), updatedAt: Date.now(), exiting: null };
        setToasts((ts) => {
            const exists = ts.some((t) => t.id === id);
            return exists ? ts.map((t) => (t.id === id ? { ...t, ...toast } : t)) : [toast, ...ts];
        });
        return id;
    }, []);
    const update = useCallback((id, patch) => {
        setToasts((ts) => ts.map((t) => {
            if (t.id !== id)
                return t;
            const tone = patch.tone ?? t.tone;
            const base = TOAST_TONES[tone] ?? TOAST_TONES.info;
            return { ...t, ...patch, tone, duration: patch.duration ?? base.duration, updatedAt: Date.now() };
        }));
    }, []);
    useEffect(() => () => timers.current.forEach((tm) => clearTimeout(tm)), []);
    const api = useMemo(() => {
        const quick = (tone) => (title, opts = {}) => show({ ...opts, tone, title });
        return {
            show,
            update,
            dismiss,
            success: quick('success'),
            error: quick('error'),
            info: quick('info'),
            warning: quick('warning'),
            loading: quick('loading'),
            /** Toast de proceso: loading → success | error con la misma tarjeta. */
            promise(promise, { loading, success, error }) {
                const id = show({ tone: 'loading', title: loading, duration: Infinity });
                Promise.resolve(promise).then((r) => update(id, { tone: 'success', ...(typeof success === 'function' ? success(r) : { title: success }) }), (e) => update(id, { tone: 'error', ...(typeof error === 'function' ? error(e) : { title: error }) }));
                return id;
            },
        };
    }, [show, update, dismiss]);
    const value = useMemo(() => ({ api, toasts, max, remove, dismiss }), [api, toasts, max, remove, dismiss]);
    return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}
/** useToast() → { show, success, error, info, warning, loading, promise, update, dismiss } */
export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx)
        throw new Error('useToast() necesita <ToastProvider> arriba en el árbol.');
    return ctx.api;
}
const PLACEMENTS = {
    'bottom-right': { box: 'bottom-4 right-4 sm:bottom-6 sm:right-6', top: false, align: 'right' },
    'bottom-center': { box: 'bottom-4 left-1/2 -translate-x-1/2 sm:bottom-6', top: false, align: 'center' },
    'top-right': { box: 'top-4 right-4 sm:top-6 sm:right-6', top: true, align: 'right' },
    'top-center': { box: 'top-4 left-1/2 -translate-x-1/2 sm:top-6', top: true, align: 'center' },
};
const GAP = 10;
const PEEK = 12;
function ToastItem({ toast, index, count, max, expanded, paused, heights, setHeight, frontHeight, offsetBefore, top, onDismiss, reduce }) {
    const ref = useRef(null);
    const [mounted, setMounted] = useState(false);
    const [drag, setDrag] = useState(0);
    const dragRef = useRef(null);
    const remaining = useRef(toast.duration);
    const startedAt = useRef(0);
    // Medir altura (la pila se calcula con alturas reales, no fijas)
    useIsoLayoutEffect(() => {
        const el = ref.current;
        if (!el)
            return undefined;
        const measure = () => setHeight(toast.id, el.offsetHeight);
        measure();
        if (typeof ResizeObserver === 'undefined')
            return undefined;
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [toast.id, setHeight]);
    useEffect(() => {
        const r = requestAnimationFrame(() => requestAnimationFrame(() => setMounted(true)));
        return () => cancelAnimationFrame(r);
    }, []);
    // Auto-cierre con pausa (hover, foco o pestaña oculta)
    useEffect(() => {
        remaining.current = toast.duration;
    }, [toast.updatedAt, toast.duration]);
    useEffect(() => {
        if (!Number.isFinite(toast.duration) || toast.exiting)
            return undefined;
        if (paused)
            return undefined;
        startedAt.current = Date.now();
        const tm = setTimeout(() => onDismiss(toast.id), Math.max(0, remaining.current));
        return () => {
            clearTimeout(tm);
            remaining.current -= Date.now() - startedAt.current;
        };
    }, [paused, toast.id, toast.duration, toast.updatedAt, toast.exiting, onDismiss]);
    const h = heights[toast.id] ?? frontHeight ?? 64;
    const dir = top ? 1 : -1;
    const visible = index < max;
    const last = useRef({ y: 0, scale: 1, clip: 0 });
    let y;
    let scale = 1;
    let clip = 0;
    if (toast.exiting) {
        // sale desde donde estaba: no se reacomoda antes de irse
        ({ y, scale, clip } = last.current);
    }
    else if (expanded) {
        y = dir * offsetBefore;
    }
    else {
        const f = frontHeight || h;
        y = top ? f + index * PEEK - h : h - f - index * PEEK;
        scale = 1 - index * 0.05;
        clip = Math.max(0, h - f);
    }
    if (!toast.exiting && mounted)
        last.current = { y, scale, clip };
    let opacity = visible ? 1 : 0;
    let x = drag;
    if (!mounted) {
        y = top ? -(h + 24) : h + 24;
        opacity = 0;
    }
    if (toast.exiting) {
        opacity = 0;
        if (toast.exiting === 'swipe-right')
            x = 420;
        else if (toast.exiting === 'swipe-left')
            x = -420;
        else {
            y += top ? -12 : 12;
            scale *= 0.96;
        }
    }
    const dragging = dragRef.current?.active;
    const transition = reduce
        ? 'opacity 160ms linear'
        : dragging
            ? 'none'
            : toast.exiting
                ? `transform ${DUR.close}ms var(--db-ease-out), opacity 260ms var(--db-ease-out), clip-path ${DUR.close}ms var(--db-ease-out)`
                : `transform ${DUR.open}ms var(--dbm-spring), opacity 260ms var(--db-ease-out), clip-path ${DUR.base}ms var(--db-ease-out)`;
    const onPointerDown = (e) => {
        if (e.button !== 0 || e.target.closest('button'))
            return;
        dragRef.current = { active: true, x0: e.clientX };
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };
    const onPointerMove = (e) => {
        if (!dragRef.current?.active)
            return;
        setDrag(e.clientX - dragRef.current.x0);
    };
    const onPointerUp = () => {
        if (!dragRef.current?.active)
            return;
        dragRef.current.active = false;
        if (Math.abs(drag) > 90)
            onDismiss(toast.id, drag > 0 ? 'swipe-right' : 'swipe-left');
        setDrag(0);
    };
    return (<li ref={ref} className="absolute inset-x-0 touch-pan-y select-none" style={{
            [top ? 'top' : 'bottom']: 0,
            zIndex: count - index,
            transformOrigin: top ? '50% 100%' : '50% 0%',
            transform: `translate3d(${x}px, ${y}px, 0) scale(${scale})`,
            opacity: dragging ? Math.max(0.35, 1 - Math.abs(drag) / 260) : opacity,
            clipPath: top ? `inset(${clip}px -48px -48px -48px)` : `inset(-48px -48px ${clip}px -48px)`,
            transition,
            pointerEvents: visible && !toast.exiting ? 'auto' : 'none',
        }} aria-hidden={visible ? undefined : 'true'} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onKeyDown={(e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onDismiss(toast.id);
            }
        }}>
      <Toast tone={toast.tone} title={toast.title} description={toast.description} action={toast.action} onAction={() => {
            toast.action?.onClick?.();
            onDismiss(toast.id);
        }} onClose={toast.dismissible === false ? undefined : () => onDismiss(toast.id)} duration={toast.duration} paused={paused} contentHidden={!expanded && index > 0} key={toast.updatedAt}/>
    </li>);
}
/**
 * ToastViewport — dónde aparecen los toasts. Uno por app (o por área con `contained`).
 * placement: bottom-right | bottom-center | top-right | top-center
 * contained: posición absoluta dentro del padre (vistas previas, paneles) en lugar de fixed.
 * F8 lleva el foco a las notificaciones; Esc cierra la enfocada.
 */
export function ToastViewport({ placement = 'bottom-right', contained = false, label = 'Notificaciones', hotkey = 'F8', className }) {
    const ctx = useContext(ToastContext);
    if (!ctx)
        throw new Error('<ToastViewport> necesita <ToastProvider>.');
    const { toasts, max, dismiss } = ctx;
    const p = PLACEMENTS[placement] ?? PLACEMENTS['bottom-right'];
    const reduce = useReducedMotion();
    const [hover, setHover] = useState(false);
    const [focus, setFocus] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [heights, setHeights] = useState({});
    const sectionRef = useRef(null);
    const [announce, setAnnounce] = useState({ polite: '', assertive: '' });
    const lastAnnounced = useRef('');
    const setHeight = useCallback((id, hgt) => setHeights((hs) => (hs[id] === hgt ? hs : { ...hs, [id]: hgt })), []);
    useEffect(() => {
        const on = () => setHidden(document.visibilityState === 'hidden');
        document.addEventListener('visibilitychange', on);
        return () => document.removeEventListener('visibilitychange', on);
    }, []);
    useEffect(() => {
        if (!hotkey)
            return undefined;
        const on = (e) => {
            if (e.key === hotkey && toasts.length) {
                e.preventDefault();
                sectionRef.current?.focus();
            }
        };
        window.addEventListener('keydown', on);
        return () => window.removeEventListener('keydown', on);
    }, [hotkey, toasts.length]);
    // Anunciar el toast más reciente (o su actualización) en la región correcta
    const newest = toasts.find((t) => !t.exiting);
    useEffect(() => {
        if (!newest)
            return;
        const key = `${newest.id}:${newest.updatedAt}`;
        if (lastAnnounced.current === key)
            return;
        lastAnnounced.current = key;
        const live = (TOAST_TONES[newest.tone] ?? TOAST_TONES.info).live;
        const text = [newest.title, newest.description].filter(Boolean).join('. ');
        setAnnounce({ polite: '', assertive: '' });
        const tm = setTimeout(() => setAnnounce(live === 'assertive' ? { polite: '', assertive: text } : { polite: text, assertive: '' }), 60);
        return () => clearTimeout(tm);
    }, [newest]);
    const live = toasts.filter((t) => !t.exiting);
    const expanded = (hover || focus) && live.length > 0;
    const paused = hover || focus || hidden;
    const frontHeight = live[0] ? heights[live[0].id] : 0;
    // Desplazamientos acumulados de los toasts vivos (el hueco se cierra cuando uno sale)
    let acc = 0;
    let liveIndex = 0;
    const offsets = {};
    const indices = {};
    for (const t of toasts) {
        if (t.exiting) {
            offsets[t.id] = acc;
            indices[t.id] = liveIndex;
            continue;
        }
        offsets[t.id] = acc;
        indices[t.id] = liveIndex;
        acc += (heights[t.id] ?? 64) + GAP;
        liveIndex++;
    }
    const visibleCount = Math.min(live.length, max);
    const listHeight = expanded
        ? live.slice(0, max).reduce((s, t) => s + (heights[t.id] ?? 64), 0) + Math.max(0, visibleCount - 1) * GAP
        : (frontHeight || 0) + Math.max(0, visibleCount - 1) * PEEK;
    return (<section ref={sectionRef} tabIndex={-1} aria-label={`${label} (${hotkey})`} className={cn(contained ? 'absolute' : 'fixed', 'z-toast w-[min(400px,calc(100%-32px))] outline-none', p.box, toasts.length === 0 && 'pointer-events-none', className)} onPointerEnter={() => setHover(true)} onPointerLeave={() => setHover(false)} onFocus={() => setFocus(true)} onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget))
                setFocus(false);
        }}>
      <ol className="relative m-0 list-none p-0" style={{ height: listHeight }}>
        {toasts.map((t) => (<ToastItem key={t.id} toast={t} index={indices[t.id]} count={toasts.length} max={max} expanded={expanded} paused={paused} heights={heights} setHeight={setHeight} frontHeight={frontHeight} offsetBefore={offsets[t.id]} top={p.top} onDismiss={dismiss} reduce={reduce}/>))}
      </ol>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{announce.polite}</div>
      <div className="sr-only" aria-live="assertive" aria-atomic="true">{announce.assertive}</div>
    </section>);
}
